import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useVerifyEmail } from '../hooks/usePrivacyMutations';
import { CONSENTTYPE } from './constant';
import { Card, SkeletonLoader, MessageBar } from './ui';

export const VerifyRequest: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isVerify, setIsVerify] = useState(false);

  const verifyEmailMutation = useVerifyEmail();

  useEffect(() => {
    const id = searchParams.get('id');

    if (!id) {
      navigate('/', { replace: true });
      return;
    }

    verifyEmailMutation.mutate(
      { id },
      {
        onSuccess: (data) => {
          if (!data.isError && data.result) {
            const requestData = data.result;

            if (requestData) {
              const user = {
                id: requestData.id,
                email: requestData.email,
                token: requestData.token || '',
                isAuthenticated: true,
                residentType: requestData.residentType,
                subjectType: requestData.subjectType,
              };
              login(user);
            }

            if (requestData && requestData.requestType === CONSENTTYPE) {
              navigate('/consentmanagement', {
                state: {
                  subjectEmail: requestData.email,
                  dataSubjectRequestId: requestData.id,
                },
                replace: true,
              });
            } else {
              setIsVerify(true);
            }
          } else {
            const message = data.responseException?.exceptionMessage || 'Verification failed';
            setErrorMessage(message);
            setTimeout(() => setErrorMessage(null), 30000);
          }
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.responseException?.exceptionMessage || 'Verification failed';
          setErrorMessage(message);
          setTimeout(() => setErrorMessage(null), 30000);
        },
      }
    );
  }, [searchParams, navigate, login, verifyEmailMutation]);

  const isLoading = verifyEmailMutation.isPending;

  return (
    <div className="max-w-md mx-auto mt-8">
      {errorMessage && (
        <div className="mb-4">
          <MessageBar variant="error">{errorMessage}</MessageBar>
        </div>
      )}

      {isLoading ? (
        <Card className="p-8">
          <SkeletonLoader variant="circle" className="mb-4" />
          <SkeletonLoader variant="text" count={3} />
        </Card>
      ) : (
        isVerify && (
          <Card className="p-8 text-center">
            {/* Green check icon */}
            <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-3">Email Verified</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Thanks for verifying your email. We value your privacy and security. Your
              request is now being processed. You will receive an update email about your request.
              You are now logged in and can submit other requests without verifying your email again.
            </p>

            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary text-white hover:bg-primary/90 rounded-lg px-6 py-2.5 text-sm font-medium transition-colors"
            >
              Go to Privacy Center
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </Card>
        )
      )}
    </div>
  );
};
